import { Injectable } from "@angular/core";
import { AbstractService } from "./abstract.service";
import { HttpClient } from "@angular/common/http";
import { Observable, BehaviorSubject } from "rxjs";
import { map, catchError, switchMap, tap } from "rxjs/operators";
import { TranslationRequest } from "src/app/shared/models/TranslationRequest";
import { MissingTranslationLog } from "src/app/shared/models/MissingTranslationRequest";
import { of } from "rxjs";

/**
 * Interface for all cache keys
 */
interface ICacheKey {
  getCacheKeyString(): string;
  equals(other: ICacheKey): boolean;
}

/**
 * Cache key for translations based on language, group, and subgroup
 */
class TranslationsCacheKey implements ICacheKey {
  constructor(
    public readonly language: string,
    public readonly group: string = '',
    public readonly subGroup: string = ''
  ) {}

  getCacheKeyString(): string {
    return `translations|lang:${this.language}|group:${this.group}|subgroup:${this.subGroup}`;
  }

  equals(other: ICacheKey): boolean {
    if (!other || !(other instanceof TranslationsCacheKey)) {
      return false;
    }
    return this.getCacheKeyString() === other.getCacheKeyString();
  }

  toString(): string {
    return this.getCacheKeyString();
  }
}

/**
 * Cache key for languages based on contract number
 */
class LanguagesCacheKey implements ICacheKey {
  constructor(public readonly contNum: number) {}

  getCacheKeyString(): string {
    return `languages|contNum:${this.contNum}`;
  }

  equals(other: ICacheKey): boolean {
    if (!other || !(other instanceof LanguagesCacheKey)) {
      return false;
    }
    return this.getCacheKeyString() === other.getCacheKeyString();
  }

  toString(): string {
    return this.getCacheKeyString();
  }
}

/**
 * Generic cache value that can store any data with expiration
 */
class CacheValue<T> {
  private cacheTime: Date;
  private readonly cacheExpiry: number; // in milliseconds

  constructor(
    public data: T,
    cacheExpiryMinutes: number = 15
  ) {
    this.cacheTime = new Date();
    this.cacheExpiry = cacheExpiryMinutes * 60 * 1000;
  }

  isExpired(): boolean {
    return (new Date().getTime() - this.cacheTime.getTime()) > this.cacheExpiry;
  }

  updateData(data: T): void {
    this.data = data;
    this.cacheTime = new Date();
  }

  getRemainingTimeMs(): number {
    const elapsed = new Date().getTime() - this.cacheTime.getTime();
    return Math.max(0, this.cacheExpiry - elapsed);
  }
}

/**
 * Generic cache manager for client-side caching
 */
class ClientCacheManager {
  private static instance: ClientCacheManager;
  private cache = new Map<string, CacheValue<any>>();

  static getInstance(): ClientCacheManager {
    if (!ClientCacheManager.instance) {
      ClientCacheManager.instance = new ClientCacheManager();
    }
    return ClientCacheManager.instance;
  }

  getOrCreate<T>(
    cacheKey: ICacheKey,
    dataLoader: () => T,
    cacheExpiryMinutes: number = 15
  ): T {
    const keyString = cacheKey.getCacheKeyString();
    const cachedValue = this.cache.get(keyString) as CacheValue<T>;

    if (cachedValue && !cachedValue.isExpired()) {
      return cachedValue.data;
    }

    const freshData = dataLoader();
    this.cache.set(keyString, new CacheValue(freshData, cacheExpiryMinutes));
    return freshData;
  }

  get<T>(cacheKey: ICacheKey): T | null {
    const keyString = cacheKey.getCacheKeyString();
    const cachedValue = this.cache.get(keyString) as CacheValue<T>;
    
    if (cachedValue && !cachedValue.isExpired()) {
      return cachedValue.data;
    }
    return null;
  }

  set<T>(cacheKey: ICacheKey, data: T, cacheExpiryMinutes: number = 15): void {
    const keyString = cacheKey.getCacheKeyString();
    this.cache.set(keyString, new CacheValue(data, cacheExpiryMinutes));
  }

  clear(cacheKey: ICacheKey): void {
    const keyString = cacheKey.getCacheKeyString();
    this.cache.delete(keyString);
  }

  clearAll(): void {
    this.cache.clear();
  }

  getStats(): { totalEntries: number; expiredEntries: number; validEntries: number } {
    let expiredCount = 0;
    let validCount = 0;

    this.cache.forEach(value => {
      if (value.isExpired()) {
        expiredCount++;
      } else {
        validCount++;
      }
    });

    return {
      totalEntries: this.cache.size,
      expiredEntries: expiredCount,
      validEntries: validCount
    };
  }
}

/**
 * Service with HTTP methods
 */
@Injectable({ providedIn: "root" })
export class TranslationService extends AbstractService {
  private cacheManager = ClientCacheManager.getInstance();
  private currentCacheKey: TranslationsCacheKey | null = null;
  
  translations: Map<string, string> = new Map();
  private translationsSubject = new BehaviorSubject<Map<string, string>>(new Map());
  public translations$ = this.translationsSubject.asObservable();

  constructor(protected override http: HttpClient) {
    super(http);
    this.loadCachedTranslations();
  }

  /**
   * Load cached translations from memory cache (no longer uses sessionStorage directly)
   */
  private loadCachedTranslations(): void {
    // This method is now primarily for initialization
    // Actual caching is handled by the ClientCacheManager
  }

  /**
   * Save translations to cache using cache key system
   */
  private saveTranslationsToCache(translationRequest: TranslationRequest): void {
    const currentLanguage = sessionStorage.getItem('languagepreference');
    if (!currentLanguage || currentLanguage === 'en' || this.translations.size === 0) {
      return;
    }

    // Save to cache manager
    if(translationRequest?.saveToCache){
        // Create cache key for this specific translation request
        const cacheKey = new TranslationsCacheKey(
          currentLanguage,
          translationRequest?.group || '',
          translationRequest?.subGroup || ''
        );
        this.cacheManager.set(cacheKey, this.translations, 15); // 15 minutes expiry
        this.currentCacheKey = cacheKey;
    }

  }

  /**
   * Clear cached translations
   */
  public clearTranslationCache(): void {
    if (this.currentCacheKey) {
      this.cacheManager.clear(this.currentCacheKey);
    }
    this.cacheManager.clearAll(); // Clear all translation caches
    this.currentCacheKey = null;
    this.translations.clear();
    this.translationsSubject.next(this.translations);
  }

  /**
   * Call this when language preference changes to clear old cache
   */
  public onLanguageChange(): void {
    const currentLanguage = sessionStorage.getItem('languagepreference');
    if (this.currentCacheKey && this.currentCacheKey.language !== currentLanguage) {
      this.clearTranslationCache();
    }
  }

  /**
   * Check if translations are already loaded for current request (language, group, subgroup)
   */
  private hasTranslationsForCurrentRequest(translationRequest: TranslationRequest): boolean {
    const currentLanguage = sessionStorage.getItem('languagepreference');
    if (!currentLanguage || currentLanguage === 'en') {
      return false;
    }

    const cacheKey = new TranslationsCacheKey(
      currentLanguage,
      translationRequest?.group || '',
      translationRequest?.subGroup || ''
    );

    const cachedTranslations = this.cacheManager.get<Map<string, string>>(cacheKey);
    return cachedTranslations !== null && cachedTranslations.size > 0;
  }

  /**
   * Get Azure translation for a given key
   * @param key The translation key that needs to be translated
   * @param translationRequest Additional context information
   * @returns Observable of the translated text from Azure or original key if translation fails
   */
  private getAzureTranslation(key: string, logRequest: MissingTranslationLog): Observable<string> {
    if(!logRequest || key === null || key === undefined || key === '' || logRequest.translationKey === null || logRequest.translationKey === undefined || logRequest.translationKey === '') {
      return of(key); // Return original key if no context
    }
    
    return super.postData<string>('/Translation/AzureTranslate', logRequest).pipe(
      map(translatedText => {
        // Return the translated text or original key if translation failed
        return translatedText && translatedText.trim() !== '' ? translatedText : key;
      }),
      catchError(error => {
        // console.error('Failed to get Azure translation:', error);
        return of(key); // Return original key on error
      })
    );
  }

  /**
   * Log missing translation key to the API and get translated value
   * @param key The translation key that was not found
   * @param translationRequest Additional context information
   * @returns Observable of the translated text from Azure or original key if translation fails
   */
  private logMissingKey(key: string, translationRequest: TranslationRequest): Observable<string> {
    if(!translationRequest || key === null || key === undefined || key === '') {
      return of(key); // Return original key if no context
    }

      const logRequest = new MissingTranslationLog({
        translationKey: key,
        language: translationRequest.language,
        translationGroup: translationRequest.group,
        translationSubGroup: translationRequest.subGroup,
        translatedValue: null
      });
    // Get Azure translation first
    return this.getAzureTranslation(key, logRequest).pipe(
      tap(translatedText => {
        // Log the missing key with the translated value (fire and forget)
        logRequest.translatedValue = translatedText;

        if(logRequest.translatedValue !== key){
        // Log to API (fire and forget - don't block the translation response)
        super.postData('/Translation/LogMissingKey', logRequest)
          .pipe(
            catchError(logError => {
              // console.error('Failed to log missing translation key:', logError);
              return of(null); // Don't propagate logging errors
            })
          )
          .subscribe(); // Fire and forget
        }
      })
    );

  }

  isLanguagePreferenceSet(): boolean
  {
    const langPref = sessionStorage.getItem('languagepreference');
    // If not set, treat as not set (return false)
    if (langPref === null || langPref === undefined || langPref === '') {
      return false;
    }

    if (langPref === 'en') {
      return false;
    }
    // Otherwise, language preference is set
    return true;
  }

  getTranslation(translationRequest: TranslationRequest): void
  {
    if(!this.isLanguagePreferenceSet())
    {
      this.translations = new Map();
      this.translationsSubject.next(this.translations);
      return;
    }

    // Check if we already have translations for the current request (language, group, subgroup)
    if (this.hasTranslationsForCurrentRequest(translationRequest)) {
      const currentLanguage = sessionStorage.getItem('languagepreference');
      const cacheKey = new TranslationsCacheKey(
        currentLanguage,
        translationRequest?.group || '',
        translationRequest?.subGroup || ''
      );
      
      const cachedTranslations = this.cacheManager.get<Map<string, string>>(cacheKey);
      if (cachedTranslations) {
        this.translations = cachedTranslations;
        this.translationsSubject.next(this.translations);
        this.currentCacheKey = cacheKey;
        return;
      }
    }

    this.translations = new Map();
    this.translationsSubject.next(this.translations);
    super.postData<{[key: string]: string}>('/Translation/GetTranslations', translationRequest).subscribe(
      (translationsObj: {[key: string]: string}) => {
        this.translations = new Map(Object.entries(translationsObj));
        this.translationsSubject.next(this.translations);
        
        // Save to cache for future use
        this.saveTranslationsToCache(translationRequest);
      },
      (error) => {
        // console.error('Error loading translations:', error);
        // console.error('Error details:', JSON.stringify(error, null, 2));
      }
    ); 
  }
  /* Non-Observable version of getTranslationForKey - Called from translation pipe
     Gets the translation for a key from the translations map
     @param key The translation key
     @param translationRequest The translation request
     @returns The translation for the key
     */
  getTranslationForKey(key: string, translationRequest: TranslationRequest = null): string{
    if(!this.translations || this.translations.size === 0)   
    {
      return key;
    }

    const translation = this.translations.get(key);
    if (!translation) {
        // For synchronous version, we can't wait for the async result
        // But we can cache the result for future use
        this.logMissingKey(key, translationRequest).subscribe(
          translatedText => {
            if (translatedText && translatedText !== key) {
              // Cache the translated text for future use
              this.translations.set(key, translatedText);
              this.translationsSubject.next(this.translations);
            }
          }
        );
      return key;
    }

    return translation;
  }

  /* Observable version of getTranslationForKey - Called from translation pipe
     Gets the translation for a key from the translations map
     @param key The translation key
     @param translationRequest The translation request
     @returns The translation for the key
     */
  getTranslationForKey$(key: string, translationRequest: TranslationRequest = null): Observable<string> {
    return this.translations$.pipe(
      switchMap(translations => {
        if(!translations || translations.size === 0) {
          return of(key);
        }
 
        const translation = translations.get(key);
        if (!translation) {
            // Return the translated text from Azure if available
            return this.logMissingKey(key, translationRequest).pipe(
              tap(translatedText => {
                // Cache the translated text for future use
                if (translatedText && translatedText !== key) {
                  this.translations.set(key, translatedText);
                  this.translationsSubject.next(this.translations);
                }
              })
            );
        }

        return of(translation);
      })
    );
  }

  /* Non-Observable version of getTranslationForKeyClientCustomization - Called from client customization pipe
     Gets the translation for a key from the translations map
     @param key The translation key
     @param valueForClientSpecific The value for client specific
     @returns The translation for the key
     */
   getTranslationForKeyClientCustomization(key: string, valueForClientSpecific: string = ''): string{
    if(valueForClientSpecific === '')
    {
      return null;
    }
    let translatedValue = this.getTranslationForKey(key);
    if(translatedValue === key)
    {
      return valueForClientSpecific;
    }
    return translatedValue;
  } 

  /* Observable version of getTranslationForKeyClientCustomization - Called from client customization pipe
     Gets the translation for a key from the translations map
     @param key The translation key
     @param valueForClientSpecific The value for client specific
     @returns The translation for the key
     */
  getTranslationForKeyClientCustomization$(key: string, valueForClientSpecific: string = ''): Observable<string> {
    if(valueForClientSpecific === '') {
      return this.translations$.pipe(map(() => null));
    }
    
    return this.getTranslationForKey$(key).pipe(
      map(translatedValue => {
        if(translatedValue === key) {
          return null;
        }
        return translatedValue === key ? null :translatedValue;
      })
    );
  }

  /**
   * Gets available languages from the API with caching
   * @returns Observable of key-value pairs representing language codes and names
   */
  getLanguages(contNum: number): Observable<{[key: string]: string}> {
    const cacheKey = new LanguagesCacheKey(contNum);
    
    // Check cache first
    const cachedLanguages = this.cacheManager.get<{[key: string]: string}>(cacheKey);
    if (cachedLanguages) {
      return of(cachedLanguages);
    }

    // Load from API and cache the result
    return super.postData<{[key: string]: string}>('/Translation/Languages', {ContNum:contNum}).pipe(
      tap(languages => {
        // Cache the languages for 15 minutes
        this.cacheManager.set(cacheKey, languages, 15);
      }),
      catchError(error => {
        // console.error('Error loading languages:', error);
        // console.error('Error details:', JSON.stringify(error, null, 2));
        // Return empty object on error
        return of({});
      })
    );
  }

  isMultiLanagueFeatureEnabled(){
    if(sessionStorage.getItem('clientData')!= undefined && sessionStorage.getItem('clientData') != null){
      const clientData = JSON.parse(sessionStorage.getItem('clientData'));
      if(clientData?.allowMultipleLanguages){
        return true;
      }
      else{
        return false;
      }
    }
    else {
      return false;
    }
  }

  
  setDefaultLanguage(langaugeCode): Observable<any> {
    if(this.isMultiLanagueFeatureEnabled()){
      sessionStorage.setItem('languagepreference',langaugeCode);
      // Call API to set language preference cookie and return the observable
      return this.setLanguagePreferenceCookie(langaugeCode);
    }
    else{
      sessionStorage.removeItem('languagepreference');
      // Return completed observable for non-multilanguage case
      return of(null);
    }
  }

  setLanguagePreferenceCookie(languageCode: string) {
    return super.postData('/Translation/SetLanguagePreference', { LanguageCode: languageCode });
  }

  getDefaultLangauge(){
    if(sessionStorage.getItem('clientData')!= undefined && sessionStorage.getItem('clientData') != null){
      if(!this.isMultiLanagueFeatureEnabled()){
        return this.getBrowserLangauge();
      }
      else{
        if(this.isLanguagePreferenceSet()){
          return sessionStorage.getItem('languagepreference');
        }
        else{
          return this.getBrowserLangauge();
        }
      }
    }
    else {
      return this.getBrowserLangauge();
    }    
  }

  getBrowserLangauge() : string{
    let browserLanguage = navigator.language || navigator.languages?.[0] || 'en';
    browserLanguage = browserLanguage.toLowerCase().startsWith('en') ? 'en' : browserLanguage.toLowerCase().substring(0, 2);
    return browserLanguage.toString();
  }

  /**
   * Clear languages cache for a specific contract number
   */
  public clearLanguagesCache(contNum: number): void {
    const cacheKey = new LanguagesCacheKey(contNum);
    this.cacheManager.clear(cacheKey);
  }

  /**
   * Get cache statistics for debugging
   */
  public getCacheStats(): { totalEntries: number; expiredEntries: number; validEntries: number } {
    return this.cacheManager.getStats();
  }
}
